import React from 'react';
import { getSdgDisplayName } from '../../../constants/sdgs';

// Import SDG images
import sdg01 from '../../../assets/SDG/E-WEB-Goal-01.png';
import sdg02 from '../../../assets/SDG/E-WEB-Goal-02.png';
import sdg03 from '../../../assets/SDG/E-WEB-Goal-03.png';
import sdg04 from '../../../assets/SDG/E-WEB-Goal-04.png';
import sdg05 from '../../../assets/SDG/E-WEB-Goal-05.png';
import sdg06 from '../../../assets/SDG/E-WEB-Goal-06.png';
import sdg07 from '../../../assets/SDG/E-WEB-Goal-07.png';
import sdg08 from '../../../assets/SDG/E-WEB-Goal-08.png';
import sdg09 from '../../../assets/SDG/E-WEB-Goal-09.png';
import sdg10 from '../../../assets/SDG/E-WEB-Goal-10.png';
import sdg11 from '../../../assets/SDG/E-WEB-Goal-11.png';
import sdg12 from '../../../assets/SDG/E-WEB-Goal-12.png';
import sdg13 from '../../../assets/SDG/E-WEB-Goal-13.png';
import sdg14 from '../../../assets/SDG/E-WEB-Goal-14.png';
import sdg15 from '../../../assets/SDG/E-WEB-Goal-15.png';
import sdg16 from '../../../assets/SDG/E-WEB-Goal-16.png';
import sdg17 from '../../../assets/SDG/E-WEB-Goal-17.png';
import sdgLogo from '../../../assets/SDG/SDGs-transparent.png';

interface SDGSelectorProps {
  selectedSDGs: number[];
  onSDGSelect: (sdg: number) => void;
}

interface SdgTileProps {
  sdgNumber: number;
  img: string;
  isSelected: boolean;
  onSelect: () => void;
  /** Keeps the label on-screen in the 3-column (mobile) and 4-column (sm+) grids. */
  labelAlignClass: string;
}

const labelAlignClass = (index: number): string => {
  const mobile = index % 3;
  const desktop = index % 4;
  const mobileClass =
    mobile === 0 ? 'left-0' : mobile === 2 ? 'right-0' : 'left-1/2 -translate-x-1/2';
  const desktopClass =
    desktop === 0
      ? 'sm:left-0 sm:right-auto sm:translate-x-0'
      : desktop === 3
        ? 'sm:left-auto sm:right-0 sm:translate-x-0'
        : 'sm:left-1/2 sm:right-auto sm:-translate-x-1/2';
  return `${mobileClass} ${desktopClass}`;
};

const SdgTile: React.FC<SdgTileProps> = ({
  sdgNumber,
  img,
  isSelected,
  onSelect,
  labelAlignClass: alignClass,
}) => {
  const name = getSdgDisplayName(sdgNumber) ?? `SDG ${sdgNumber}`;

  return (
    <div
      className={`group relative cursor-pointer rounded-lg transition-all duration-200 hover:z-30 ${
        isSelected ? 'scale-105 ring-4 ring-emerald-500' : ''
      }`}
      onClick={onSelect}
    >
      <img
        src={img}
        alt={`SDG Goal ${sdgNumber}`}
        className={`h-auto w-full rounded-lg transition-[filter] ${
          isSelected ? '' : 'grayscale opacity-60'
        }`}
      />
      <div
        role="tooltip"
        className={`pointer-events-none absolute top-full z-30 mt-2 w-max max-w-[15rem] rounded-md border-2 border-dark-950 bg-white px-3 py-2 text-center text-base font-semibold leading-snug text-dark-950 opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 sm:max-w-[18rem] sm:text-lg ${alignClass}`}
      >
        {name}
      </div>
    </div>
  );
};

export const SDGSelector: React.FC<SDGSelectorProps> = ({ selectedSDGs, onSDGSelect }) => {
  // SDG icons array
  const sdgImages = [
    sdg01,
    sdg02,
    sdg03,
    sdg04,
    sdg05,
    sdg06,
    sdg07,
    sdg08,
    sdg09,
    sdg10,
    sdg11,
    sdg12,
    sdg13,
    sdg14,
    sdg15,
    sdg16,
    sdg17,
  ];

  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 sm:gap-4">
      {sdgImages.map((img, index) => {
        const sdgNumber = index + 1;

        return (
          <SdgTile
            key={sdgNumber}
            sdgNumber={sdgNumber}
            img={img}
            isSelected={selectedSDGs.includes(sdgNumber)}
            onSelect={() => onSDGSelect(sdgNumber)}
            labelAlignClass={labelAlignClass(index)}
          />
        );
      })}
      <div className="col-span-2 sm:col-span-3 flex items-center justify-center">
        <img src={sdgLogo} alt="SDGs" className="w-full h-auto max-w-md" />
      </div>
    </div>
  );
};

export default SDGSelector;
